import { FC, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ViewMode } from './types';
import { formatDisplayDate, formatMonthYear, getStartOfWeek, getEndOfWeek } from './dateUtils';

interface DateNavigationProps {
  currentDate: Date;
  viewMode: ViewMode;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
}

export const DateNavigation: FC<DateNavigationProps> = ({
  currentDate,
  viewMode,
  onPrevious,
  onNext,
  onToday,
}) => {
  const displayText = useMemo(() => {
    switch (viewMode) {
      case 'day':
        return formatDisplayDate(currentDate);
      case 'week': {
        const start = getStartOfWeek(currentDate);
        const end = getEndOfWeek(currentDate);
        const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const endStr = end.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
        return `${startStr} - ${endStr}`;
      }
      case 'month':
        return formatMonthYear(currentDate);
    }
  }, [currentDate, viewMode]);

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onPrevious}
        className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        aria-label="Previous"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={onToday}
        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
      >
        Today
      </button>
      <button
        onClick={onNext}
        className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        aria-label="Next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      <span className="ml-2 text-lg font-semibold text-gray-900 dark:text-slate-100 min-w-[200px]">
        {displayText}
      </span>
    </div>
  );
};
